/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  Atom,
  useStateX,
  useStateXAtom,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import { Box, Button, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useSaveContext } from 'common/Context';
import { constants } from 'componentregistry';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import {
  darkModeToggleAtom,
  fieldVariantAtom,
  fullscreenToggleAtom,
  headerNavigationAtom,
  notificationLocationAtom,
  showRoleNameAtom,
  showRoleSwitcherAtom,
} from 'components/TextField/BaseField';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import {
  autoPageRefreshAtom,
  closeSidebarOnPageNavigationAtom,
  debugLogPreferenceAtom,
  developerModePreferenceAtom,
  floatingSidebarsAtom,
  hideTipsAtom,
  disableMessageCompressionAtom,
  useAppID,
} from 'designer/atoms';
import { activePinnedSidebarPanelAtom } from 'layout/Sidebar/atoms';
import { kebabCase } from 'lodash-es';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo } from 'react';
import useUser, { isImpersonatedDeveloper } from 'root/useUser';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { isNull } from 'utils/isEmpty';
import { MenuItemPulse } from 'utils/Pulse';
import {
  applicationDarkModeAtom,
  componentTipAtom,
  notificationLocationUserSettingsAtom,
  sidebarLocationPrefAtom,
  userDarkModeAtom,
  userDirectionAtom,
} from 'utils/state';

const { fieldVariant, notificationLocation } = constants;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    padding: 0,
    width: '100%',
    position: 'relative',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
    minWidth: 140,
  },
  label: {
    minWidth: 180,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  option: {
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    fontSize: '0.8rem',
    outline: 'none',
    overflow: 'hidden',
    padding: '4px 0',
    position: 'relative',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    transition: 'color 0.2s',
    whiteSpace: 'nowrap',
    '& > svg': {
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    margin: '0 4px',
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
}));

function Option<T>({
  value,
  label,
  selected,
  onSelect,
}: {
  value: T;
  label: string;
  selected?: boolean;
  onSelect: (val: T) => void;
}) {
  const classes = useStyles();
  const onClick = () => onSelect(value);
  return (
    <div
      onKeyDown={(e) => clickOnEnterOrSpace(e, onClick)}
      role="button"
      data-testid={value}
      tabIndex={0}
      onClick={onClick}
      className={clsx(classes.option, selected ? classes.selected : null)}
    >
      {label}
    </div>
  );
}

interface BooleanProps {
  label: string;
  helpText?: string;
  atom: Atom<boolean>;
  options: Record<string, boolean>;
  pkKey: 'userName' | 'appUid';
  pkValue: string;
  pulseMessage?: string;
  pulseKey?: string;
  category?: string;
}

export function BooleanOptionSettings({
  label,
  options,
  atom: _atom,
  helpText,
  pkKey,
  pkValue,
  pulseKey,
  pulseMessage,
  category,
}: BooleanProps) {
  const classes = useStyles();
  const [value, setValue] = useStateXAtom(_atom, {
    params: { [pkKey]: pkValue },
  });
  const appUid = useAppID();
  const platform = usePlatform();
  const { setContextValue } = useSaveContext();
  async function setOption(val: boolean) {
    setValue(val);
    if (category === 'Application Settings') {
      setContextValue(
        _atom.path[4] as string,
        val,
        _atom.path[1] as string,
        [_atom.path[4]],
        pkKey,
        pkValue,
      );
    } else {
      await platform.postAtom(
        {
          ds: _atom.path[1] as string,
          key: pkValue,
          path: [_atom.path[4]],
          value: val,
          pk: { [pkKey]: pkValue },
        },
        { appUid },
      );
    }
  }

  const keys = Object.keys(options);
  const count = keys.length;
  const idx = value ? 0 : 1;
  return (
    <div className={classes.root} data-testid={kebabCase(label)}>
      <div
        data-tip-at="left"
        data-tip={label.length > 30 ? label : undefined}
        className={classes.label}
      >
        {label}
      </div>
      <div data-tip={helpText} className={classes.inner}>
        <div
          style={{
            width: `calc(${100 / count}% - 10px)`,
            left: `${(100 / count) * idx}%`,
          }}
          className={classes.s}
        />
        {keys.map((key) => {
          const lbl = options[key] ? 'On' : 'Off';
          return (
            <Option
              key={key}
              value={options[key]}
              label={lbl}
              onSelect={setOption}
              selected={value === options[key]}
            />
          );
        })}
      </div>
      {pulseMessage && pulseKey ? (
        <MenuItemPulse pulseKey={pulseKey} pulseMessage={pulseMessage} />
      ) : null}
    </div>
  );
}

interface InternalProps<T extends string> {
  label: string;
  helpText?: string;
  value: T;
  onChange: (val: T) => void;
  options: Record<T, string>;
}

export function SelectSettingsInternal<T extends string>({
  label,
  options,
  value,
  onChange,
  helpText,
}: InternalProps<T>) {
  const keys = Object.keys(options) as T[];
  const _options = keys.map((k) => ({
    value: k,
    label: options[k],
  }));
  const platform = usePlatform();
  return (
    <VirtualizedSelectInput
      variant="standard"
      fullWidth
      value={value}
      options={_options}
      onChange={(_value) => onChange(_value as T)}
      label={label}
      placeholder={'Choose an option'}
      helpText={helpText}
      disabled={isImpersonatedDeveloper(platform)}
    />
  );
}

export function StringOptionSettingsInternal<T extends string>({
  label,
  options,
  value,
  onChange,
  helpText,
}: InternalProps<T>) {
  const classes = useStyles();
  const keys = Object.keys(options) as T[];
  if (keys.length > 3) {
    return (
      <SelectSettingsInternal
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        helpText={helpText}
      />
    );
  }
  const count = keys.length;
  const idx = keys.indexOf(value);
  return (
    <div className={classes.root}>
      <div
        data-tip-at="left"
        data-tip={label.length > 30 ? label : undefined}
        className={classes.label}
      >
        {label}
      </div>
      <div data-tip={helpText} className={classes.inner}>
        <Box
          sx={{
            width: `calc(${100 / count}% - 10px)`,
            left: `${(100 / count) * idx}%`,
          }}
          className={classes.s}
        />
        {keys.map((key) => {
          const lbl = options[key];
          return (
            <Option
              key={key}
              value={key as T}
              label={lbl}
              onSelect={onChange}
              selected={value === key}
            />
          );
        })}
      </div>
    </div>
  );
}

interface Props<T extends string> {
  label: string;
  helpText?: string;
  atom: Atom<T>;
  options: Record<T, string>;
  pkKey: 'userName' | 'appUid';
  pkValue: string;
  category?: string;
}

function StringOptionSettings<T extends string>({
  label,
  options,
  atom: _atom,
  helpText,
  pkKey,
  pkValue,
  category,
}: Props<T>) {
  const [value, setValue] = useStateXAtom(_atom, {
    params: { [pkKey]: pkValue },
  });
  const appUid = useAppID();
  const platform = usePlatform();
  const { setContextValue } = useSaveContext();
  async function setOption(val: T) {
    setValue(val);
    if (category === 'Application Settings') {
      setContextValue(
        _atom.path[4] as string,
        val,
        _atom.path[1] as string,
        [_atom.path[4]],
        pkKey,
        pkValue,
      );
    } else {
      await platform.postAtom(
        {
          ds: _atom.path[1] as string,
          key: pkValue,
          path: [_atom.path[4]],
          value: val,
          pk: { [pkKey]: pkValue },
        },
        { appUid },
      );
    }
  }

  return (
    <StringOptionSettingsInternal
      value={value}
      label={label}
      helpText={helpText}
      onChange={setOption}
      options={options}
    />
  );
}

export const FieldVariantSettings = memo(function FieldVariantSettings() {
  const appUid = useAppID();
  return (
    <StringOptionSettings
      label="Field Variant"
      options={fieldVariant}
      atom={fieldVariantAtom}
      pkKey="appUid"
      pkValue={appUid}
      category={'Application Settings'}
    />
  );
});

export const NotificationLocationSettings = memo(
  function NotificationLocationSettings() {
    const appUid = useAppID();
    return (
      <StringOptionSettings
        label="Notification Location"
        options={notificationLocation}
        atom={notificationLocationAtom}
        pkKey="appUid"
        pkValue={appUid}
        category={'Application Settings'}
      />
    );
  },
);

export const sidebarLocationVariant = {
  start: 'Start',
  end: 'End',
} as const;

export const SidebarLocationSettings = memo(function SidebarLocationSettings() {
  const { userName } = useUser();
  return (
    <StringOptionSettings
      label="Sidebar Location"
      options={sidebarLocationVariant}
      atom={sidebarLocationPrefAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const pinnedPanelVariant = {
  HIDDEN: 'None',
  props: 'Property',
  tree: 'Page Tree',
} as const;

export const enableDebugConsoleVariant = {
  Hide: 'Hidden',
  Show: 'Show',
  Floating: 'Floating',
} as const;

export const enableDebugConsoleAtom = atom<
  keyof typeof enableDebugConsoleVariant
>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'enableDebugConsole'],
  defaultValue: 'Hide',
});

export const PinPropertyPanelSettings = memo(
  function PinPropertyPanelSettings() {
    const { userName } = useUser();

    return (
      <StringOptionSettings
        label="Pinned Panel"
        // @ts-ignore partial options
        options={pinnedPanelVariant}
        atom={activePinnedSidebarPanelAtom}
        pkKey="userName"
        pkValue={userName}
      />
    );
  },
);

export const DarkModeSettings = memo(function DarkModeSetting() {
  const { userName } = useUser();
  return (
    <StringOptionSettings
      label="Appearance"
      options={toOptions(['light', 'dark'])}
      atom={userDarkModeAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const AppLevelDarkMode = memo(function AppLevelDarkMode() {
  const appUid = useAppID();
  return (
    <StringOptionSettings
      label="Appearance"
      options={toOptions(['light', 'dark', 'system'])}
      atom={applicationDarkModeAtom}
      pkKey="appUid"
      pkValue={appUid}
    />
  );
});

export const DirectionSettings = memo(function DirectionSettings() {
  const { userName } = useUser();
  return (
    <StringOptionSettings
      label="Direction"
      options={{ ltr: 'LTR', rtl: 'RTL' }}
      atom={userDirectionAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const NotificationLocationUserSettings = memo(
  function NotificationLocationUserSettings() {
    const { userName } = useUser();
    return (
      <StringOptionSettings
        label="Notification Location"
        options={{ ...notificationLocation, '': 'App Settings' }}
        atom={notificationLocationUserSettingsAtom}
        pkKey="userName"
        pkValue={userName}
      />
    );
  },
);

export const onOffVariant = {
  true: true,
  false: false,
} as const;

export const DeveloperModeSettings = memo(function DeveloperModeSettings() {
  const { userName } = useUser();
  return (
    <BooleanOptionSettings
      label="Developer Mode"
      options={onOffVariant}
      atom={developerModePreferenceAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const DebugLogSettings = memo(function DebugLogSettings() {
  const { userName } = useUser();
  return (
    <BooleanOptionSettings
      label="Debug Log"
      options={onOffVariant}
      atom={debugLogPreferenceAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const PulseStateSettings = memo(function PulseStateSettings() {
  const { userName } = useUser();
  const classes = useStyles();
  const [value, setValue] = useStateX(
    ['db', 'IoUserSettings', userName, 'md', 'pulse'],
    {},
  );
  const appUid = useAppID();
  const platform = usePlatform();

  return (
    <div className={classes.root}>
      <div className={classes.label}>Reset Tips</div>
      <Button
        disabled={isNull(value) || Object.keys(value).length === 0}
        onClick={async (e) => {
          setValue({});
          await platform.postAtom(
            {
              ds: 'IoUserSettings',
              key: userName,
              path: ['pulse'],
              value: {},
              pk: { userName },
            },
            { appUid },
          );
        }}
      >
        Reset
      </Button>
    </div>
  );
});

export const FloatingSidebarsSettings = memo(
  function FloatingSidebarsSettings() {
    const { userName } = useUser();
    const state = useStateXAtomValue(floatingSidebarsAtom, {
      params: { userName },
    });
    return (
      <>
        <BooleanOptionSettings
          label="Floating Panels"
          options={onOffVariant}
          atom={floatingSidebarsAtom}
          pkKey="userName"
          pkValue={userName}
          pulseKey="floatingPanels"
          pulseMessage={
            state
              ? 'Turn this off to view the entire content of the page'
              : undefined
          }
        />
      </>
    );
  },
);

export const HideTipsSettings = memo(function HideTipsSettings() {
  const { userName } = useUser();

  return (
    <BooleanOptionSettings
      label="Hide Developer Tips"
      options={onOffVariant}
      atom={hideTipsAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const HighlightComponentsSettings = memo(
  function HighlightComponentsSettings() {
    const { userName } = useUser();

    return (
      <BooleanOptionSettings
        label="Highlight Components"
        options={onOffVariant}
        atom={componentTipAtom}
        pkKey="userName"
        pkValue={userName}
      />
    );
  },
);

export const CloseSidebarOnPageNavigationSettings = memo(
  function CloseSidebarOnPageNavigationSettings() {
    const { userName } = useUser();
    return (
      <BooleanOptionSettings
        label="Close Sidebar On Page Navigation"
        options={onOffVariant}
        atom={closeSidebarOnPageNavigationAtom}
        pkKey="userName"
        pkValue={userName}
      />
    );
  },
);

export const AutoPageRefreshSettings = memo(function AutoPageRefreshSettings() {
  const { userName } = useUser();
  return (
    <BooleanOptionSettings
      label="Auto Page Refresh"
      options={onOffVariant}
      atom={autoPageRefreshAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const CompressionSettings = memo(function CompressionSettings() {
  const { userName } = useUser();
  return (
    <BooleanOptionSettings
      label="Disable WebSocket Message Compression"
      options={onOffVariant}
      atom={disableMessageCompressionAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const DebugConsoleSettings = memo(function DebugConsoleSettings() {
  const { userName } = useUser();
  return (
    <StringOptionSettings
      label="Show Debug Console"
      options={enableDebugConsoleVariant}
      atom={enableDebugConsoleAtom}
      pkKey="userName"
      pkValue={userName}
    />
  );
});

export const HeaderFullScreenToggleSettings = memo(
  function HeaderFullScreenToggleSettings() {
    const appUid = useAppID();
    return (
      <>
        <BooleanOptionSettings
          label="Full Screen Toggle"
          options={onOffVariant}
          atom={fullscreenToggleAtom}
          pkKey="appUid"
          pkValue={appUid}
          category={'Application Settings'}
        />
      </>
    );
  },
);

export const HeaderNavigationSettings = memo(
  function HeaderNavigationSettings() {
    const appUid = useAppID();
    return (
      <>
        <BooleanOptionSettings
          label="Navigation Icon"
          options={onOffVariant}
          atom={headerNavigationAtom}
          pkKey="appUid"
          pkValue={appUid}
          category="Application Settings"
        />
      </>
    );
  },
);

export const HeaderDarkModeToggleSettings = memo(
  function HeaderDarkModeToggleSettings() {
    const appUid = useAppID();
    return (
      <>
        <BooleanOptionSettings
          label="Dark Mode Toggle"
          options={onOffVariant}
          atom={darkModeToggleAtom}
          pkKey="appUid"
          pkValue={appUid}
          category="Application Settings"
        />
      </>
    );
  },
);

export const RoleSwitcherSettings = memo(function RoleSwitcherSettings() {
  const appUid = useAppID();
  return (
    <>
      <BooleanOptionSettings
        label="Role Switcher"
        options={onOffVariant}
        atom={showRoleSwitcherAtom}
        pkKey="appUid"
        pkValue={appUid}
        category="Application Settings"
      />
    </>
  );
});

export const ShowRoleNameSettings = memo(function ShowRoleNameSettings() {
  const appUid = useAppID();
  return (
    <>
      <BooleanOptionSettings
        label="Show Role Name"
        options={onOffVariant}
        atom={showRoleNameAtom}
        pkKey="appUid"
        pkValue={appUid}
        category="Application Settings"
      />
    </>
  );
});
